import { ActionConfirmation, usePublishActionOutcome, type ActionOutcome } from "../ui/ActionConfirmation.js";
import { useEffect, useMemo, useState, type ComponentProps, type FormEvent } from "react";
import type { BookingOffering } from "./api.js";
import { TutorBookLesson as TutorBookLessonCore } from "./TutorBookLessonCore.js";
import { TutorBookLessonExperience } from "./TutorBookLessonExperience.js";
import {
  getTutorBookingContact,
  updateTutorBookingContact,
  type TutorBookingContactPatch,
  type TutorBookingContactStatus,
} from "./tutorBookingContact.js";
import { tutorOneOffBooking } from "./tutorOneOffBooking.js";
import { TeachingLinkHelp } from "./TeachingLinkHelp.js";

export type TutorBookLessonWithSetupProps = ComponentProps<typeof TutorBookLessonCore>;

type SetupOffering = BookingOffering & { assignment_setup_required?: boolean };

function needsSetup(offering: BookingOffering): offering is SetupOffering {
  return (offering as SetupOffering).assignment_setup_required === true;
}

function ownerContactMessage(status: TutorBookingContactStatus): string {
  if (status.reason === "payer_link_conflict") {
    return "The stored booking payer link conflicts with the student record. An owner needs to resolve it before another private booking.";
  }
  if (status.reason === "school_not_configured") {
    return "This booking payer needs owner setup before a private lesson can be booked.";
  }
  if (status.reason === "payer_name_incomplete" && status.mode === "self") {
    return "The student's name cannot be used as a booking customer yet. An owner needs to correct the student record before booking.";
  }
  return "Booking contact details need an owner to complete before this private lesson can be booked.";
}

/**
 * The two deliberately narrow setup gates in front of the canonical booking
 * component.
 *
 * 1. A tutor-created student may still be missing the parent/student contact
 *    facts the Hub needs to resolve a real Amelia customer. The Hub decides
 *    readiness and exactly which missing fields this tutor may fill. This does
 *    not expose or edit payer kind, customer id, school or recipient settings.
 * 2. Once booking identity is usable, a pristine tutor-created student may
 *    still need their first canonical tutor/service relationship. WMF-SIte
 *    returns real Amelia services marked `assignment_setup_required`; choosing
 *    one creates only that relationship.
 *
 * After both gates are satisfied, the shared upcoming / historical booking
 * experience runs. Neither setup step creates an appointment, consumes a credit
 * or processes a payment.
 */
export function TutorBookLessonWithSetup(props: TutorBookLessonWithSetupProps) {
  const { studentId, selectedTutorProviderId = null, onMutationBusyChange } = props;
  const publish = usePublishActionOutcome();
  const [setupOutcome, setSetupOutcome] = useState<ActionOutcome | null>(null);
  function report(result: ActionOutcome) { if (!publish(result)) setSetupOutcome(result); }
  const [contact, setContact] = useState<TutorBookingContactStatus | null>(null);
  const [contactBusy, setContactBusy] = useState(false);
  const [contactProblem, setContactProblem] = useState<string | null>(null);
  const [parentName, setParentName] = useState("");
  const [parentEmail, setParentEmail] = useState("");
  const [studentEmail, setStudentEmail] = useState("");

  const [setupOfferings, setSetupOfferings] = useState<SetupOffering[] | null>(null);
  const [assignmentReady, setAssignmentReady] = useState(false);
  const [assignmentBusy, setAssignmentBusy] = useState(false);
  const [assignmentProblem, setAssignmentProblem] = useState<string | null>(null);
  /** No bookable or set-up service at all: the tutor has no teaching link yet. */
  const [noLink, setNoLink] = useState(false);
  const [offeringsRevision, setOfferingsRevision] = useState(0);

  useEffect(() => {
    let current = true;
    setSetupOutcome(null);
    setContact(null);
    setContactProblem(null);
    setParentName("");
    setParentEmail("");
    setStudentEmail("");
    setSetupOfferings(null);
    setAssignmentReady(false);
    setAssignmentProblem(null);

    void getTutorBookingContact(studentId).then(
      (result) => {
        if (!current) return;
        setContact(result);
      },
      (cause: unknown) => {
        if (!current) return;
        setContactProblem(
          cause instanceof Error
            ? cause.message
            : "Could not check whether this student is ready for private booking.",
        );
      },
    );

    return () => {
      current = false;
      onMutationBusyChange?.(false);
    };
  }, [onMutationBusyChange, studentId]);

  useEffect(() => {
    // Offerings and contact readiness are independent read-only prerequisites.
    // Start both together so ready students do not pay their network latency in
    // series. Rendering below still keeps contact readiness authoritative: an
    // early offerings result cannot expose setup or booking UI while contact
    // details remain incomplete.
    let current = true;
    setAssignmentReady(false);
    setSetupOfferings(null);
    setAssignmentProblem(null);
    setNoLink(false);
    void tutorOneOffBooking.offerings(studentId, selectedTutorProviderId).then(
      (result) => {
        if (!current) return;
        if (result.offerings.length === 0) {
          setSetupOfferings([]);
          setNoLink(true);
          return;
        }
        const candidates = result.offerings.filter(
          (offering): offering is SetupOffering =>
            needsSetup(offering) &&
            (selectedTutorProviderId === null || offering.provider_id === selectedTutorProviderId),
        );
        setSetupOfferings(candidates);
        setAssignmentReady(candidates.length === 0);
      },
      (cause: unknown) => {
        if (!current) return;
        setAssignmentProblem(
          cause instanceof Error
            ? cause.message
            : "Could not check the student's lesson setup.",
        );
      },
    );
    return () => {
      current = false;
      onMutationBusyChange?.(false);
    };
  }, [offeringsRevision, onMutationBusyChange, selectedTutorProviderId, studentId]);

  const unique = useMemo(() => {
    const seen = new Set<string>();
    return (setupOfferings ?? []).filter((offering) => {
      const key = `${offering.provider_id}:${offering.service_id}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [setupOfferings]);

  async function saveContact(event: FormEvent) {
    event.preventDefault();
    if (contact === null || !contact.editable || contactBusy) return;

    const patch: TutorBookingContactPatch = {};
    if (contact.missing_fields.includes("parentName")) patch.parentName = parentName;
    if (contact.missing_fields.includes("parentEmail")) patch.parentEmail = parentEmail;
    if (contact.missing_fields.includes("studentEmail")) patch.studentEmail = studentEmail;

    setContactBusy(true);
    setContactProblem(null);
    onMutationBusyChange?.(true);
    try {
      const next = await updateTutorBookingContact(studentId, patch);
      setContact(next);
      report({ variant: next.ready ? "success" : "attention", title: "Booking contact details saved", description: props.studentName, details: [{ value: next.ready ? "The contact details are ready for booking. No lesson was booked by this action." : "Another booking contact detail is still needed. No lesson was booked." }] });
      if (!next.ready) {
        setContactProblem(
          "Those details were saved, but the student still needs another booking contact detail before a lesson can be booked.",
        );
      }
    } catch (cause: unknown) {
      setContactProblem(
        cause instanceof Error
          ? cause.message
          : "The booking contact details could not be saved. Nothing was booked.",
      );
    } finally {
      setContactBusy(false);
      onMutationBusyChange?.(false);
    }
  }

  async function establish(offering: SetupOffering) {
    if (assignmentBusy) return;
    setAssignmentBusy(true);
    setAssignmentProblem(null);
    onMutationBusyChange?.(true);
    try {
      await tutorOneOffBooking.establishTeachingAssignment(studentId, {
        provider_id: offering.provider_id,
        service_id: offering.service_id,
      });
      setSetupOfferings([]);
      setAssignmentReady(true);
      report({ variant: "success", title: "Teaching assignment created", description: props.studentName, details: [{ value: offering.service_name ?? "Lesson service" }, { value: "No lesson was booked by this setup action." }] });
    } catch (cause: unknown) {
      setAssignmentProblem(
        cause instanceof Error
          ? cause.message
          : "The first teaching service could not be set up. Nothing was booked.",
      );
    } finally {
      setAssignmentBusy(false);
      onMutationBusyChange?.(false);
    }
  }

  if (contact === null) {
    return (
      <div className="panel">
        {setupOutcome && <ActionConfirmation {...setupOutcome} />}
        <h3 style={{ marginTop: 0 }}>Book a lesson</h3>
        <p className="empty">Checking booking contact details…</p>
        {contactProblem && <div className="error">{contactProblem}</div>}
      </div>
    );
  }

  if (!contact.ready) {
    if (!contact.editable) {
      return (
        <div className="panel">
        {setupOutcome && <ActionConfirmation {...setupOutcome} />}
          <h3 style={{ marginTop: 0 }}>Book a lesson</h3>
          <div className="notice">
            <strong>Booking contact details need attention</strong>
            <p className="small" style={{ marginBottom: 0 }}>
              {ownerContactMessage(contact)}
            </p>
          </div>
          {contactProblem && <div className="error" style={{ marginTop: 10 }}>{contactProblem}</div>}
        </div>
      );
    }

    return (
      <form className="panel" onSubmit={(event) => void saveContact(event)}>
        <h3 style={{ marginTop: 0 }}>Add booking contact details</h3>
        <p className="small muted">
          This student can stay on your list with just a name, but a real parent or student email is needed before the booking system can create their first private lesson. This step only fills the missing contact details; it does not book or charge anything.
        </p>

        {contactProblem && <div className="error" style={{ marginBottom: 10 }}>{contactProblem}</div>}

        <div className="fields">
          {contact.missing_fields.includes("parentName") && (
            <label>
              <span className="small muted">Parent name</span>
              <input
                name="booking-parent-name"
                value={parentName}
                onChange={(event) => setParentName(event.target.value)}
                required
                disabled={contactBusy}
                autoFocus
              />
            </label>
          )}
          {contact.missing_fields.includes("parentEmail") && (
            <label>
              <span className="small muted">Parent email</span>
              <input
                name="booking-parent-email"
                type="email"
                value={parentEmail}
                onChange={(event) => setParentEmail(event.target.value)}
                required
                disabled={contactBusy}
                autoCapitalize="none"
                spellCheck={false}
              />
            </label>
          )}
          {contact.missing_fields.includes("studentEmail") && (
            <label>
              <span className="small muted">Student email</span>
              <input
                name="booking-student-email"
                type="email"
                value={studentEmail}
                onChange={(event) => setStudentEmail(event.target.value)}
                required
                disabled={contactBusy}
                autoCapitalize="none"
                spellCheck={false}
              />
            </label>
          )}
        </div>

        <button type="submit" disabled={contactBusy} style={{ marginTop: 10 }}>
          {contactBusy ? "Saving…" : "Save and continue"}
        </button>
      </form>
    );
  }

  if (noLink) {
    return (
      <TeachingLinkHelp
        studentId={studentId}
        studentName={props.studentName ?? "this pupil"}
        bookingBlocked
        onLinked={() => setOfferingsRevision((revision) => revision + 1)}
      />
    );
  }

  if (assignmentReady) return <>
    {setupOutcome && <ActionConfirmation {...setupOutcome} />}
    {props.appointmentMode ? <TutorBookLessonCore {...props} /> : <TutorBookLessonExperience {...props} />}
  </>;

  if (setupOfferings === null) {
    return (
      <div className="panel">
        {setupOutcome && <ActionConfirmation {...setupOutcome} />}
        <h3 style={{ marginTop: 0 }}>Book a lesson</h3>
        <p className="empty">Checking this student&apos;s lesson setup…</p>
        {assignmentProblem && <div className="error">{assignmentProblem}</div>}
      </div>
    );
  }

  if (unique.length === 0) {
    return (
      <div className="panel">
        <h3 style={{ marginTop: 0 }}>Book a lesson</h3>
        <div className="error">
          {assignmentProblem ?? "No first lesson service is available in your current tutor scope."}
        </div>
      </div>
    );
  }

  return (
    <div className="panel">
      <h3 style={{ marginTop: 0 }}>Choose the first lesson service</h3>
      <p className="small muted">
        This student was just added to your list. Choose the service you are starting with; the Hub will record that teaching relationship before showing available times. Nothing is booked or charged at this step.
      </p>
      {assignmentProblem && <div className="error" style={{ marginBottom: 10 }}>{assignmentProblem}</div>}
      <div className="row" style={{ flexWrap: "wrap" }}>
        {unique.map((offering) => (
          <button
            key={`${offering.provider_id}:${offering.service_id}`}
            type="button"
            disabled={assignmentBusy}
            onClick={() => void establish(offering)}
          >
            {assignmentBusy ? "Setting up…" : offering.service_name ?? `Lesson service ${offering.service_id}`}
          </button>
        ))}
      </div>
    </div>
  );
}
